import { CallbotMaterial, CallbotMaterialFilter, CallbotMaterialStatus } from '@/types/callbot'

// Тексты и форматы — дословно из макета DH_Аудиорассылки_2026-09-21_v2.

export const MATERIAL_STATUS_LABELS: Record<CallbotMaterialStatus, string> = {
    approved: 'Одобрено',
    pending: 'На модерации',
    rejected: 'Отклонено',
}

export const MATERIAL_FILTERS: CallbotMaterialFilter[] = ['all', 'approved', 'pending', 'rejected']

export const MATERIAL_ACCEPT = '.mp3,.wav,.ogg,.m4a,audio/mpeg,audio/wav,audio/ogg,audio/mp4'
export const MATERIAL_FORMAT_HINT = 'MP3, WAV, OGG, M4A · до 25 секунд · до 10 МБ'
export const MATERIAL_NAME_MAX_LENGTH = 80

const MAX_BYTES = 10 * 1024 * 1024
const MAX_SECONDS = 25.05

// Повторные дозвоны поверх первого звонка. В сервис уходит общее число
// попыток (maxAttempts = повторы + 1), поэтому value — это попытки.
export const REDIAL_OPTIONS = [
    { value: 1, label: 'Без повторного дозвона' },
    { value: 2, label: '1 повторный дозвон' },
    { value: 3, label: '2 повторных дозвона' },
    { value: 4, label: '3 повторных дозвона' },
    { value: 5, label: '4 повторных дозвона' },
]

export function redialLabel(maxAttempts: number, intervalMinutes: number): string {
    const redials = Math.max(0, maxAttempts - 1)
    if (!redials) return 'Без повторного дозвона'
    const option = REDIAL_OPTIONS.find((item) => item.value === maxAttempts)
    return `${option ? option.label : `${redials} повторных`} · через ${intervalMinutes} минут`
}

// Интервал между попытками дозвона, минут.
export const RETRY_INTERVALS = [5, 10, 15, 20]

// 1 ролик, 2 ролика, 5 роликов.
export function formatMaterialCount(count: number): string {
    const mod10 = count % 10
    const mod100 = count % 100
    const word =
        mod10 === 1 && mod100 !== 11
            ? 'ролик'
            : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
              ? 'ролика'
              : 'роликов'
    return `${count} ${word}`
}

export function formatMaterialDuration(seconds: number): string {
    const total = Math.round(seconds || 0)
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

export function formatMaterialSize(bytes: number): string {
    return bytes >= 1048576
        ? `${(bytes / 1048576).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} МБ`
        : `${Math.ceil(bytes / 1024)} КБ`
}

export function formatMaterialDate(value: string): string {
    return new Date(value).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })
}

export function materialFilterCounts(items: Pick<CallbotMaterial, 'status'>[]) {
    return {
        all: items.length,
        approved: items.filter(item => item.status === 'approved').length,
        pending: items.filter(item => item.status === 'pending').length,
        rejected: items.filter(item => item.status === 'rejected').length,
    }
}

export function defaultMaterialName(fileName: string): string {
    return fileName
        .replace(/\.[^.]+$/, '')
        .replace(/_/g, ' ')
        .slice(0, MATERIAL_NAME_MAX_LENGTH)
}

export function precheckAudioFile(file: Pick<File, 'name' | 'size'>): string | null {
    if (!/\.(mp3|wav|ogg|m4a)$/i.test(file.name)) {
        return 'Поддерживаются MP3, WAV, OGG и M4A. Выберите другой файл.'
    }
    if (!file.size) return 'Файл пустой. Выберите другую запись.'
    if (file.size > MAX_BYTES) {
        return 'Файл больше 10 МБ. Сожмите запись или выберите другой файл.'
    }
    return null
}

export function validateAudioDuration(duration: number): string | null {
    if (!Number.isFinite(duration) || duration <= 0) {
        return 'Не удалось определить длительность. Сохраните запись как MP3 или WAV и загрузите снова.'
    }
    if (duration > MAX_SECONDS) {
        return `Запись длится ${Math.ceil(
            duration
        )} сек. Максимум — 25 секунд. Сократите ролик и загрузите снова.`
    }
    return null
}

export const AUDIO_READ_ERROR =
    'Не удалось прочитать аудио. Файл может быть повреждён или не поддерживаться. Попробуйте MP3 или WAV.'

// Длительность — из метаданных браузера, как в макете: сервер всё равно
// перепроверит, но пользователь узнаёт о слишком длинной записи сразу.
export function readAudioDurationInBrowser(url: string, timeoutMs = 10000): Promise<number> {
    return new Promise((resolve, reject) => {
        const media = new Audio()
        let settled = false
        const timer = setTimeout(() => finish(new Error('timeout')), timeoutMs)
        function finish(error?: Error) {
            if (settled) return
            settled = true
            clearTimeout(timer)
            const duration = media.duration
            media.removeAttribute('src')
            media.load()
            if (error) reject(error)
            else resolve(duration)
        }
        media.preload = 'metadata'
        media.onloadedmetadata = () => finish()
        media.onerror = () => finish(new Error('decode'))
        media.src = url
    })
}
